import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../api/api';
import { formatDisplayValue } from '../utils/formatUtils';
import './WellKPIConfiguration.css';

const WellKPIConfiguration = () => {
  // State management
  const [configColumns, setConfigColumns] = useState([]);
  const [kpiConfigurations, setKpiConfigurations] = useState([]);
  const [newKpi, setNewKpi] = useState({
    title: '',
    subtitle: '',
    icon: '📊',
    tableName: '',
    columnName: '',
    calculationMethod: 'sum',
    unit: '',
    format: 'comma', // Default format
    critical: false,
    enabled: true
  });
  const [editingKpi, setEditingKpi] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  const [tableSearchTerm, setTableSearchTerm] = useState('');

  const role = localStorage.getItem('role') || 'admin';

  // Available calculation methods
  const calculationMethods = [
    { value: 'sum', label: 'Sum (Total)', description: 'Add all values together' },
    { value: 'average', label: 'Average (Mean)', description: 'Calculate average of all values' },
    { value: 'max', label: 'Maximum', description: 'Get the highest value' },
    { value: 'min', label: 'Minimum', description: 'Get the lowest value' },
    { value: 'count', label: 'Count', description: 'Count number of records' },
    { value: 'latest', label: 'Latest Value', description: 'Get the most recent value' },
    { value: 'first', label: 'First Value', description: 'Get the earliest value' }
  ];

  // Available number formats (based on formatUtils.js)
  const formatOptions = [
    { value: 'comma', label: 'Number with Commas', description: 'e.g., 1,234.56', example: '1,234.56' },
    { value: 'decimal2', label: '2 Decimal Places', description: 'e.g., 1234.56', example: '1234.56' },
    { value: 'decimal3', label: '3 Decimal Places', description: 'e.g., 1234.567', example: '1234.567' },
    { value: 'decimal4', label: '4 Decimal Places', description: 'e.g., 1234.5678', example: '1234.5678' },
    { value: 'int', label: 'Integer', description: 'e.g., 1,234 (no decimals)', example: '1,234' },
    { value: 'float', label: 'Float', description: 'e.g., 1,234.5678 (2-4 decimals)', example: '1,234.5678' },
    { value: 'currency', label: 'Currency', description: 'e.g., $1,234.56 (based on unit)', example: '$1,234.56' },
    { value: 'string', label: 'Raw Value', description: 'Display as-is without formatting', example: '1234.5678' }
  ];

  // Available icons for KPIs
  const availableIcons = [
    '📊', '📈', '📉', '💰', '⏱️', '📏', '📅', '⚡', '🔧', '📋',
    '🎯', '📐', '⚖️', '🧭', '📎', '🔍', '⚙️', '🛢️', '🔩', '📦'
  ];

  // Load initial data
  useEffect(() => {
    loadConfigColumns();
    loadKpiConfigurations();
  }, []);

  const loadConfigColumns = async () => {
    try {
      setIsLoading(true);
      const response = await apiFetch('/api/config/columns');
      setConfigColumns(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load column configurations:', error);
      showToast('❌ Failed to load column configurations');
    } finally {
      setIsLoading(false);
    }
  };

  const loadKpiConfigurations = async () => {
    try {
      const response = await apiFetch('/api/config/kpi-configurations');
      setKpiConfigurations(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to load KPI configurations:', error);
      // Initialize with default KPIs if none exist
      initializeDefaultKpis();
    }
  };

  const initializeDefaultKpis = () => {
    const defaultKpis = [
      {
        id: 'current-td',
        title: 'Current TD',
        subtitle: 'Measured Depth',
        icon: '📏',
        tableName: 'DailyWellData',
        columnName: 'ActualTD',
        calculationMethod: 'latest',
        unit: 'm',
        format: 'decimal2',
        critical: true,
        enabled: true,
        order: 1
      },
      {
        id: 'total-cost',
        title: 'Total Cost',
        subtitle: 'Cumulative',
        icon: '💰',
        tableName: 'DailyWellData',
        columnName: 'DailyCost',
        calculationMethod: 'sum',
        unit: 'USD',
        format: 'currency',
        critical: true,
        enabled: true,
        order: 3
      },
      {
        id: 'avg-rop',
        title: 'Avg ROP',
        subtitle: 'Rate of Penetration',
        icon: '⚡',
        tableName: 'DrillingParameters',
        columnName: 'ROP',
        calculationMethod: 'average',
        unit: 'm/hr',
        format: 'decimal3',
        critical: false,
        enabled: true,
        order: 5
      },
      {
        id: 'npt-hours',
        title: 'NPT Hours',
        subtitle: 'Non-Productive Time',
        icon: '⏱️',
        tableName: 'DailyWellData',
        columnName: 'NPT_Hours',
        calculationMethod: 'sum',
        unit: 'hrs',
        format: 'decimal2',
        critical: false,
        enabled: true,
        order: 4
      }
    ];
    setKpiConfigurations(defaultKpis);
  };

  const saveKpiConfiguration = async (kpiData) => {
    try {
      const method = kpiData.id ? 'PUT' : 'POST';
      const url = kpiData.id 
        ? `/api/config/kpi-configurations/${kpiData.id}`
        : '/api/config/kpi-configurations';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(kpiData)
      });

      showToast('✅ KPI configuration saved successfully');
      await loadKpiConfigurations();
      return response;
    } catch (error) {
      console.error('Failed to save KPI configuration:', error);
      showToast('❌ Failed to save KPI configuration');
      throw error;
    }
  };

  const deleteKpiConfiguration = async (kpiId) => {
    try {
      await apiFetch(`/api/config/kpi-configurations/${kpiId}`, {
        method: 'DELETE'
      });
      showToast('✅ KPI configuration deleted successfully');
      await loadKpiConfigurations();
    } catch (error) {
      console.error('Failed to delete KPI configuration:', error);
      showToast('❌ Failed to delete KPI configuration');
    }
  };

  const handleAddKpi = () => {
    setNewKpi({
      title: '',
      subtitle: '',
      icon: '📊',
      tableName: '',
      columnName: '',
      calculationMethod: 'sum',
      unit: '',
      format: 'comma', // Default format
      critical: false,
      enabled: true
    });
    setEditingKpi(null);
    setTableSearchTerm(''); // Reset table search
    setShowAddForm(true);
  };

  const handleEditKpi = (kpi) => {
    setNewKpi({ ...kpi });
    setEditingKpi(kpi);
    setTableSearchTerm(''); // Reset table search
    setShowAddForm(true);
  };

  const handleSaveKpi = async () => {
    if (!newKpi.title || !newKpi.tableName || !newKpi.columnName) {
      showToast('❌ Please fill in all required fields');
      return;
    }

    try {
      const kpiData = {
        ...newKpi,
        id: editingKpi?.id || `kpi-${Date.now()}`,
        order: editingKpi?.order || kpiConfigurations.length + 1
      };

      await saveKpiConfiguration(kpiData);
      setShowAddForm(false);
      setEditingKpi(null);
    } catch (error) {
      // Error already handled in saveKpiConfiguration
    }
  };

  const handleDeleteKpi = async (kpiId) => {
    if (!window.confirm('Are you sure you want to delete this KPI configuration?')) {
      return;
    }
    await deleteKpiConfiguration(kpiId);
  };

  const toggleKpiStatus = async (kpi) => {
    const updatedKpi = { ...kpi, enabled: !kpi.enabled };
    await saveKpiConfiguration(updatedKpi);
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const getAvailableColumns = () => {
    if (!selectedTable) return [];
    const table = configColumns.find(t => t.tableName === selectedTable);
    return table ? table.columns : [];
  };

  const getColumnDetails = (tableName, columnName) => {
    const table = configColumns.find(t => t.tableName === tableName);
    if (!table) return null;
    const column = table.columns.find(c => c.name === columnName);
    return column;
  };

  // Smart function to determine if a table can be used for well KPIs
  const getWellRelatedTables = () => {
    const wellRelatedTables = [];
    
    console.log('Analyzing tables for well relationships...');
    console.log('Total config columns:', configColumns.length);
    
    configColumns.forEach(table => {
      if (!table.columns || !Array.isArray(table.columns)) {
        console.log(`Skipping table ${table.tableName} - no columns array`);
        return;
      }
      
      const hasWellID = table.columns.some(col => 
        col.name.toLowerCase().includes('wellid') || 
        col.name.toLowerCase().includes('well_id') ||
        col.name.toLowerCase() === 'wellid' ||
        col.name.toLowerCase() === 'well_id'
      );
      
      const hasReportID = table.columns.some(col => 
        col.name.toLowerCase().includes('reportid') || 
        col.name.toLowerCase().includes('report_id') ||
        col.name.toLowerCase() === 'reportid' ||
        col.name.toLowerCase() === 'report_id'
      );
      
      // Log analysis for each table
      console.log(`Table: ${table.tableName}`, {
        hasWellID,
        hasReportID,
        columnNames: table.columns.map(c => c.name),
        totalColumns: table.columns.length
      });
      
      // Determine relationship type
      let relationshipType = '';
      let description = '';
      
      if (hasWellID && !hasReportID) {
        relationshipType = 'Direct (WellID)';
        description = 'Directly linked to well';
      } else if (hasReportID && !hasWellID) {
        relationshipType = 'Indirect (ReportID)';
        description = 'Linked via daily reports';
      } else if (hasWellID && hasReportID) {
        relationshipType = 'Both (WellID + ReportID)';
        description = 'Linked to both well and reports';
      }
      
      // Only include tables that have at least one relationship
      if (hasWellID || hasReportID) {
        const tableInfo = {
          name: table.tableName,
          displayName: table.displayName || table.tableName,
          relationshipType,
          description,
          hasWellID,
          hasReportID,
          columnCount: table.columns.length
        };
        wellRelatedTables.push(tableInfo);
        console.log(`✅ Added table: ${table.tableName} (${relationshipType})`);
      } else {
        console.log(`❌ Excluded table: ${table.tableName} - no WellID or ReportID`);
      }
    });
    
    // Sort by relationship type (Direct first, then Indirect)
    const sortedTables = wellRelatedTables.sort((a, b) => {
      if (a.hasWellID && !b.hasWellID) return -1;
      if (!a.hasWellID && b.hasWellID) return 1;
      return a.name.localeCompare(b.name);
    });
    
    console.log('Final well-related tables:', sortedTables.map(t => `${t.name} (${t.relationshipType})`));
    
    return sortedTables;
  };

  const filteredKpis = kpiConfigurations.filter(kpi =>
    kpi.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kpi.tableName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    kpi.columnName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableTables = getWellRelatedTables();
  
  const filteredTables = availableTables.filter(table =>
    table.name.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
    table.displayName.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
    table.relationshipType.toLowerCase().includes(tableSearchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="kpi-config-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading KPI configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="kpi-config-container">
      {/* Header */}
      <div className="kpi-config-header">
        <div className="header-main">
          <h1>⚙️ Well KPI Configuration</h1>
          <p>Configure Key Performance Indicators displayed on Well Detail pages</p>
        </div>
        <div className="header-actions">
          {(role === 'admin' || role === 'read-write') && (
            <button className="add-kpi-btn" onClick={handleAddKpi}>
              ➕ Add KPI
            </button>
          )}
        </div>
      </div>

      {/* Smart Table Detection Info */}
      <div className="info-section">
        <div className="info-header">
          <h3>🧠 Smart Table Detection</h3>
          <p>Only tables that can be linked to wells are shown based on database relationships:</p>
        </div>
        <div className="relationship-types">
          <div className="relationship-type">
            <span className="relationship-badge direct">Direct (WellID)</span>
            <span className="relationship-desc">Tables directly linked to wells (e.g., Well, BHA, CasingRun)</span>
          </div>
          <div className="relationship-type">
            <span className="relationship-badge indirect">Indirect (ReportID)</span>
            <span className="relationship-desc">Tables linked via daily reports (e.g., DrillingParameters, DailyWellData)</span>
          </div>
          <div className="relationship-type">
            <span className="relationship-badge both">Both (WellID + ReportID)</span>
            <span className="relationship-desc">Tables with dual relationships</span>
          </div>
        </div>
        <div className="table-count">
          <strong>{availableTables.length} tables available</strong> out of {configColumns.length} total tables
        </div>
      </div>

      {/* Search and Filters */}
      <div className="kpi-config-filters">
        <div className="search-group">
          <input
            type="text"
            placeholder="Search KPIs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-stats">
          {filteredKpis.length} of {kpiConfigurations.length} KPIs
          {' • '}
          {kpiConfigurations.filter(k => k.enabled).length} enabled
        </div>
      </div>

      {/* KPI List */}
      <div className="kpi-list">
        {filteredKpis.map((kpi, index) => {
          const columnDetails = getColumnDetails(kpi.tableName, kpi.columnName);
          return (
            <div key={kpi.id || index} className={`kpi-item ${!kpi.enabled ? 'disabled' : ''}`}>
              <div className="kpi-item-header">
                <div className="kpi-basic-info">
                  <div className="kpi-icon">{kpi.icon}</div>
                  <div className="kpi-title-group">
                    <h3 className="kpi-title">{kpi.title}</h3>
                    <span className="kpi-subtitle">{kpi.subtitle}</span>
                  </div>
                  {kpi.critical && <span className="critical-badge">Critical</span>}
                </div>
                <div className="kpi-status-toggle">
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={kpi.enabled}
                      onChange={() => toggleKpiStatus(kpi)}
                      disabled={role === 'read-only'}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              <div className="kpi-item-details">
                <div className="kpi-mapping">
                  <span className="mapping-label">Data Source:</span>
                  <span className="mapping-value">
                    {kpi.tableName}.{kpi.columnName}
                  </span>
                </div>
                <div className="kpi-calculation">
                  <span className="calculation-label">Method:</span>
                  <span className="calculation-value">
                    {calculationMethods.find(m => m.value === kpi.calculationMethod)?.label || kpi.calculationMethod}
                  </span>
                </div>
                <div className="kpi-format">
                  <span className="format-label">Format:</span>
                  <span className="format-value">
                    {formatOptions.find(f => f.value === kpi.format)?.label || kpi.format || 'Default'}
                    {formatOptions.find(f => f.value === kpi.format)?.example && (
                      <span className="format-example"> • {formatOptions.find(f => f.value === kpi.format)?.example}</span>
                    )}
                  </span>
                </div>
                <div className="kpi-relationship">
                  <span className="relationship-label">Relationship:</span>
                  <span className="relationship-value">
                    {(() => {
                      const tableInfo = availableTables.find(t => t.name === kpi.tableName);
                      return tableInfo ? tableInfo.relationshipType : 'Unknown';
                    })()}
                  </span>
                </div>
                {columnDetails && (
                  <div className="kpi-column-info">
                    <span className="column-info-label">Column:</span>
                    <span className="column-info-value">
                      {columnDetails.label} ({columnDetails.type})
                      {columnDetails.unit && ` • Unit: ${columnDetails.unit}`}
                    </span>
                  </div>
                )}
              </div>

              {(role === 'admin' || role === 'read-write') && (
                <div className="kpi-item-actions">
                  <button
                    className="edit-btn"
                    onClick={() => handleEditKpi(kpi)}
                    title="Edit KPI"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteKpi(kpi.id)}
                    title="Delete KPI"
                  >
                    🗑️ Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit KPI Form Modal */}
      {showAddForm && (
        <div className="kpi-form-overlay">
          <div className="kpi-form-modal">
            <div className="kpi-form-header">
              <h2>{editingKpi ? 'Edit KPI Configuration' : 'Add New KPI Configuration'}</h2>
              <button className="close-btn" onClick={() => setShowAddForm(false)}>✕</button>
            </div>

            <div className="kpi-form-content">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>KPI Title *</label>
                    <input
                      type="text"
                      value={newKpi.title}
                      onChange={(e) => setNewKpi({...newKpi, title: e.target.value})}
                      placeholder="e.g., Current TD"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Subtitle</label>
                    <input
                      type="text"
                      value={newKpi.subtitle}
                      onChange={(e) => setNewKpi({...newKpi, subtitle: e.target.value})}
                      placeholder="e.g., Measured Depth"
                    />
                  </div>
                  <div className="form-group">
                    <label>Icon</label>
                    <div className="icon-selector">
                      {availableIcons.map(icon => (
                        <button
                          key={icon}
                          type="button"
                          className={`icon-option ${newKpi.icon === icon ? 'selected' : ''}`}
                          onClick={() => setNewKpi({...newKpi, icon})}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <input
                      type="text"
                      value={newKpi.unit}
                      onChange={(e) => setNewKpi({...newKpi, unit: e.target.value})}
                      placeholder="e.g., m, hrs, USD"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Data Mapping</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Table Search</label>
                    <input
                      type="text"
                      value={tableSearchTerm}
                      onChange={(e) => setTableSearchTerm(e.target.value)}
                      placeholder="Search tables... (e.g., DailyWellData, Drilling)"
                      className="table-search-input"
                    />
                    <small className="form-help">
                      Filter tables by name or relationship type
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Table *</label>
                    <select
                      value={newKpi.tableName}
                      onChange={(e) => {
                        setNewKpi({...newKpi, tableName: e.target.value, columnName: ''});
                        setSelectedTable(e.target.value);
                      }}
                      required
                      size="8"
                      className="table-select"
                    >
                      <option value="">-- Select a table --</option>
                      {filteredTables.map((table, index) => (
                        <option key={table.name} value={table.name} title={`${table.description} • ${table.relationshipType} • ${table.columnCount} columns`}>
                          {table.displayName} ({table.relationshipType})
                        </option>
                      ))}
                    </select>
                    <div className="table-select-info">
                      Showing {filteredTables.length} of {availableTables.length} tables with well relationships
                    </div>
                    {newKpi.tableName && (
                      <small className="form-help">
                        {availableTables.find(t => t.name === newKpi.tableName)?.description}
                        {' • '}
                        Relationship: {availableTables.find(t => t.name === newKpi.tableName)?.relationshipType}
                        {' • '}
                        {availableTables.find(t => t.name === newKpi.tableName)?.columnCount} columns
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Column *</label>
                    <select
                      value={newKpi.columnName}
                      onChange={(e) => setNewKpi({...newKpi, columnName: e.target.value})}
                      disabled={!newKpi.tableName}
                      required
                    >
                      <option value="">Select a column...</option>
                      {getAvailableColumns().map(column => (
                        <option key={column.name} value={column.name}>
                          {column.label} ({column.type}) {column.unit && `- ${column.unit}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Calculation Method *</label>
                    <select
                      value={newKpi.calculationMethod}
                      onChange={(e) => setNewKpi({...newKpi, calculationMethod: e.target.value})}
                      required
                    >
                      {calculationMethods.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </select>
                    <small className="form-help">
                      {calculationMethods.find(m => m.value === newKpi.calculationMethod)?.description}
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Display Options</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Number Format *</label>
                    <select
                      value={newKpi.format}
                      onChange={(e) => setNewKpi({...newKpi, format: e.target.value})}
                      required
                    >
                      {formatOptions.map(format => (
                        <option key={format.value} value={format.value}>
                          {format.label} ({format.example})
                        </option>
                      ))}
                    </select>
                    <small className="form-help">
                      {formatOptions.find(f => f.value === newKpi.format)?.description}
                    </small>
                  </div>
                </div>
                <div className="form-checkboxes">
                  <label className="checkbox-group">
                    <input
                      type="checkbox"
                      checked={newKpi.critical}
                      onChange={(e) => setNewKpi({...newKpi, critical: e.target.checked})}
                    />
                    <span>Mark as Critical (highlight with special styling)</span>
                  </label>
                  <label className="checkbox-group">
                    <input
                      type="checkbox"
                      checked={newKpi.enabled}
                      onChange={(e) => setNewKpi({...newKpi, enabled: e.target.checked})}
                    />
                    <span>Enable this KPI (show on Well Detail pages)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="kpi-form-actions">
              <button className="cancel-btn" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleSaveKpi}>
                {editingKpi ? 'Update KPI' : 'Save KPI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Message */}
      {toast && (
        <div className="toast-message">
          {toast}
        </div>
      )}
    </div>
  );
};

export default WellKPIConfiguration; 